import React from "react";
import { useAuth } from "@/context/AuthContext";
import PortalShell from "@/portal/PortalShell";
import { usePortal } from "@/portal/PortalContext";
import { ErrorState, Skeleton } from "@/portal/ui/Layout";
import { greeting } from "@/portal/format";
import { FolderOpen } from "lucide-react";
import { HomeKpis } from "./home/Kpis";
import { FeaturedProject } from "./home/FeaturedProject";
import { KeyStagesCard, UpcomingMilestonesCard } from "./home/MilestoneCards";
import { BudgetCard } from "./home/BudgetCard";
import { DocumentsCard } from "./home/DocumentsCard";
import { PhotosCard } from "./home/PhotosCard";
import { RisksCard } from "./home/RisksCard";
import { TasksCard, ActivityCard } from "./home/ActivityCards";

export default function ClientHome() {
  const { user } = useAuth();
  const { overview: ov, error, refresh } = usePortal();
  const first = user?.name?.split(" ")[0] || "";

  return (
    <PortalShell title="Acasă">
      <header className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div className="min-w-0">
          <h1 className="cp-display" data-testid="page-title">{greeting()}, {first}.</h1>
          <p className="cp-body-lg mt-1.5 max-w-2xl">Ai o vedere clară asupra progresului, costurilor și deciziilor esențiale din proiectele tale.</p>
        </div>
        <p className="hidden lg:block cp-serif text-[17px] leading-snug text-cp-navy shrink-0 pb-1" data-testid="home-tagline">
          Construim<br />mai mult decât clădiri.<br />Construim încredere.
        </p>
      </header>
      {error && !ov ? <ErrorState onRetry={refresh} /> : !ov ? <HomeSkeleton /> : <HomeBody ov={ov} />}
    </PortalShell>
  );
}

/** Ordonează proiectele: cele mai multe decizii în așteptare, apoi cel mai recent actualizat. */
function orderProjects(projects) {
  return [...projects].sort((a, b) => (b.pending_decisions || 0) - (a.pending_decisions || 0)
    || String(b.last_update || "").localeCompare(String(a.last_update || "")));
}

/** Buline de navigare între proiecte, în stil iPhone. */
function ProjectDots({ projects, index, onSelect }) {
  if (projects.length < 2) return null;
  const active = projects[index];
  return (
    <nav className="flex flex-col items-center gap-3 pt-4 pb-1" aria-label="Navigare între proiecte" data-testid="home-project-dots">
      <div className="cp-meta text-center">
        Proiect {index + 1} din {projects.length}
        {active?.name ? <> · <span className="text-cp-text font-medium">{active.name}</span></> : null}
      </div>
      <div className="flex items-center gap-2.5">
        {projects.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onSelect(i)}
            aria-label={`Vezi proiectul ${p.name}`}
            aria-current={i === index}
            title={p.name}
            data-testid={`home-project-dot-${i}`}
            className={`rounded-full transition-all duration-300 focus-visible:outline-offset-2 ${
              i === index
                ? "w-6 h-2.5 bg-cp-navy"
                : "w-2.5 h-2.5 bg-cp-border-strong hover:bg-cp-text-3"
            }`}
          />
        ))}
      </div>
    </nav>
  );
}

function HomeBody({ ov }) {
  const ordered = React.useMemo(() => orderProjects(ov.projects || []), [ov.projects]);
  const [idx, setIdx] = React.useState(0);
  React.useEffect(() => { if (idx > ordered.length - 1) setIdx(0); }, [ordered.length, idx]);

  const active = ordered[Math.min(idx, ordered.length - 1)];

  if (!active) {
    return (
      <div className="cp-panel p-10 text-center" data-testid="no-projects">
        <FolderOpen className="h-8 w-8 mx-auto text-cp-text-3 mb-3" aria-hidden="true" />
        <p className="cp-h3">Nu ai încă acces la niciun proiect</p>
        <p className="cp-body mt-1">Contactează Project Managerul Brisk pentru a primi acces.</p>
      </div>
    );
  }

  const forPid = (arr) => (arr || []).filter((x) => x.project_id === active.id);
  const activeMs = (ov.milestones || []).filter((m) => m.project_id === active.id);
  const budget = ov.budgets?.[active.id] || null;

  return (
    <div className="space-y-4">
      <HomeKpis ov={ov} featured={active} />

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,2.25fr)_minmax(0,0.9fr)_minmax(0,1.25fr)] gap-4">
        <FeaturedProject p={active} />
        <KeyStagesCard project={active} milestones={activeMs} />
        <BudgetCard project={active} budget={budget} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <DocumentsCard reviewDocs={forPid(ov.documents_review)} recentDocs={forPid(ov.recent_documents)} />
        <PhotosCard photos={forPid(ov.recent_photos)} total={active.photos_count} project={active} />
        <RisksCard risks={forPid(ov.risks)} project={active} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <TasksCard decisions={forPid(ov.pending_decisions)} />
        <ActivityCard activity={forPid(ov.activity)} project={active} />
        <UpcomingMilestonesCard milestones={activeMs} multiProject={false} />
      </div>

      <ProjectDots projects={ordered} index={Math.min(idx, ordered.length - 1)} onSelect={setIdx} />
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[78px]" />)}</div>
      <div className="grid grid-cols-1 xl:grid-cols-[2.1fr_1fr_1.3fr] gap-4"><Skeleton className="h-[280px]" /><Skeleton className="h-[280px]" /><Skeleton className="h-[280px]" /></div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-[200px]" />)}</div>
    </div>
  );
}
